import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { PromptValue } from '@azure/msal-common';
import { AccessGuard } from '@app/access/access-guard';
import { emailFirstSignIn, loginRequest } from '@app/auth-config';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { AuthenticationService } from '../services/auth.service';
import { LoginUser } from '../models/user.model';

@Injectable({
    providedIn: 'root',
})
export class UserGuard {

    constructor(private auth: AuthenticationService) {
        this.auth.logoutObs.subscribe((isLogout: boolean) => {
            AccessGuard.accessMethods = {};
            if (isLogout) { UserGuard.isExecuted = false; }
        });
    }

    public static isExecuted = false;

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean> {
        if (UserGuard.isExecuted) { // To preveint multiple calls
            return of(true);
        }
        return this.auth.getTokenInfo().pipe(
            tap(userInfo => {
                const currentUser = this.auth.currentUser;
                // Writing a null currentUser back through the setter REMOVES loginUser
                // from localStorage, recreating the missing-stored-user state that leaves
                // MSAL signed in with nothing persisted on our side. Only persist when
                // there is something to persist.
                if (!currentUser) { return; }
                LoginUser.setUserInfo(currentUser, userInfo);
                this.auth.currentUser = currentUser;
            }),
            switchMap((userInfo) => {
                // Deactivated user presenting a still-valid token: the backend zeroes the session
                // (every guarded call 401/403), so force a logout rather than load a broken app.
                if (userInfo && userInfo.isActive === false) {
                    UserGuard.isExecuted = false;
                    this.auth.logout();
                    return of(false);
                }
                AccessGuard.accessMethods = {};
                return this.auth.getAccessContollers().pipe(// Should execute only once
                    switchMap((data) => {
                        try {
                            UserGuard.isExecuted = true;
                            AccessGuard.accesss = data;
                            return of(true);
                        } catch (e) {
                            // this.auth.logout();
                            UserGuard.isExecuted = false;
                            return of(false);
                        }
                    }), catchError((e) => {
                        // this.auth.logout();
                        UserGuard.isExecuted = false;
                        this.showLoadFailed(e);
                        return of(false);
                    })
                );
            }),
            // Safety net: without this, a failing getTokenInfo() (a 401 the interceptor
            // could not repair, or a network error) escapes canActivate as a router
            // NavigationError. That cancels navigation with nothing rendered — a blank
            // page with no menu and no way to sign out. Returning false cancels
            // navigation cleanly; user-facing recovery is the interceptor's Session
            // Expired modal, which re-authenticates in place.
            catchError((e) => {
                UserGuard.isExecuted = false;
                this.showLoadFailed(e);
                return of(false);
            })
        );
    }

    private loadFailedShown = false;

    /**
     * The account could not be loaded, so navigation is cancelled and nothing renders. Say so
     * instead of leaving a blank page (production, 2026-10-22: a stale token after an idle sign-out
     * gave the loader then white). Only for an HTTP error the interceptor passed through - a 401 it
     * handled arrives as a plain Error and already has its own Session Expired / sign-in modal.
     */
    private showLoadFailed(e: unknown): void {
        if (!(e instanceof HttpErrorResponse) || this.auth.isLoggingOut || this.loadFailedShown) {
            return;
        }
        this.loadFailedShown = true;
        // Status 0 = the API could not be reached: signing in again would not help, so offer a retry.
        const unreachable = e.status === 0;
        Swal.fire({
            title: unreachable ? "Can't reach Evide" : "We couldn't load your account",
            text: unreachable
                ? 'Please check your connection and try again.'
                : 'Please sign in again to continue.',
            icon: 'warning',
            confirmButtonText: unreachable ? 'Try again' : 'Sign in again',
            allowOutsideClick: false,
            allowEscapeKey: false,
            customClass: { confirmButton: 'btn btn-success' },
            buttonsStyling: false
        }).then(() => {
            this.loadFailedShown = false;
            if (unreachable) {
                window.location.reload();
            } else {
                // prompt=login so a token the API keeps refusing can't loop silently through B2C.
                // Email first: back through Evide's email page with the stale sign-in cleared (same reason as
                // the interceptor's Session Expired box - a different account must be choosable).
                this.auth.restartSignIn(true);
            }
        });
    }

    canActivateChild(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Promise<boolean> | Observable<boolean> | boolean {
        return this.canActivate(route, state);
    }
}
